import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';
import TimeSelect from '../components/TimeSelect';
import { findUnresolvedConflicts, findCoverageGaps } from '../utils/scheduleConflict';

const API = 'https://api.cpms-srlkevcharge.com';
const OCPP_BASE_URL = API.replace('https://', 'wss://').replace('http://', 'ws://');

// ── Icons ─────────────────────────────────────────────────────────────────
const IconChevron = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IconEdit = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);
const IconTag = () => (
  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M20.59 13.41L11 3.82V3H3v8l.82.82L13.41 20.6a2 2 0 002.83 0l4.34-4.34a2 2 0 000-2.83z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const IconBuilding = () => (
  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" />
    <path d="M9 9v.01" /><path d="M9 12v.01" /><path d="M9 15v.01" /><path d="M9 18v.01" />
  </svg>
);
const IconPin = () => (
  <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const IconPlugSmall = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M18 7V5a2 2 0 00-2-2H8a2 2 0 00-2 2v2M12 22V13" /><path d="M6 7h12a2 2 0 012 2v3a5 5 0 01-5 5H9a5 5 0 01-5-5V9a2 2 0 012-2z" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconTrash = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);
const IconTrashStation = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);
const IconQrCode = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <line x1="14" y1="14" x2="14" y2="17" /><line x1="14" y1="20" x2="14" y2="21" />
    <line x1="17" y1="14" x2="21" y2="14" /><line x1="17" y1="17" x2="17" y2="17" />
    <line x1="17" y1="20" x2="21" y2="20" /><line x1="21" y1="17" x2="21" y2="21" />
  </svg>
);
const IconX = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconDownload = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

function StatusBadge({ status }) {
  const s = (status || 'offline').toLowerCase();
  return <span className={`badge badge-${s}`}>{status}</span>;
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Math.floor((Date.now() - new Date(dateStr + 'Z')) / 1000);
  if (diff < 30 && diff > -30) return 'Just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr + 'Z').toLocaleDateString('en-GB');
}

function QrCodeModal({ charger, onClose }) {
  const qrUrl = `${API}/chargers/${charger.id}/qr-code`;

  const handleDownload = async () => {
    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `QR_${charger.ocpp_id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download QR code', err);
    }
  };

  return (
    <div className="qr-modal-overlay" onClick={onClose}>
      <div className="qr-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="qr-modal-header">
          <div>
            <h3>Charger QR Code</h3>
            <p>{charger.ocpp_id}</p>
          </div>
          <button className="qr-modal-close" onClick={onClose}>
            <IconX />
          </button>
        </div>
        <div className="qr-modal-body">
          <img src={qrUrl} alt="Charger QR Code" />
          <div className="qr-modal-code">{charger.qr_code_id}</div>
        </div>
        <div className="qr-modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-green" onClick={handleDownload}>
            <IconDownload /> Download
          </button>
        </div>
      </div>
    </div>
  );
}

function OcppSetupModal({ charger, onClose }) {
  const ocppUrl = `${OCPP_BASE_URL}/ocpp/${charger.ocpp_id}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ocppUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy OCPP URL', err);
    }
  };

  return (
    <div className="qr-modal-overlay" onClick={onClose}>
      <div className="qr-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="qr-modal-header">
          <div>
            <h3>OCPP Setup</h3>
            <p>{charger.ocpp_id}</p>
          </div>
          <button className="qr-modal-close" onClick={onClose}>
            <IconX />
          </button>
        </div>
        <div className="qr-modal-body" style={{ display: 'block', textAlign: 'left' }}>
          <div className="ocpp-url-label">OCPP Connection URL</div>
          <div className="ocpp-url-row">
            <div className="ocpp-url-text">{ocppUrl}</div>
            <button className="ocpp-url-copy-btn" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="ocpp-url-hint">Enter this as the OCPP backend URL in the charger's own configuration settings.</div>
        </div>
        <div className="qr-modal-actions">
          <button className="btn btn-green" onClick={onClose} style={{ width: '100%' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

const LOCATION_EDIT_FIELDS = [
  { name: 'station_name', label: 'Station Name', type: 'text' },
  { name: 'address', label: 'Address', type: 'text' },
  { name: 'city', label: 'City', type: 'text' },
  { name: 'country', label: 'Country', type: 'text' },
  { name: 'zipcode', label: 'Post Code', type: 'text' },
  { name: 'network_type', label: 'Network Type', type: 'select', options: ['Public', 'Private', 'Restricted Network'] },
  { name: 'parking_type', label: 'Parking Type', type: 'select', options: ['On Street Parking', 'Off Street Parking', 'Private', 'Public'] },
  { name: 'free_charging', label: 'Free Charging', type: 'select', options: ['Yes', 'No'] },
  { name: 'amenities', label: 'Amenities', type: 'text' },
];

function LocationEditPanel({ location, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    station_name: location.station_name || '',
    address: location.address || '',
    city: location.city || '',
    country: location.country || '',
    zipcode: location.zipcode || '',
    network_type: location.network_type || '',
    parking_type: location.parking_type || '',
    free_charging: location.free_charging || '',
    amenities: location.amenities || '',
    description: location.description || '',
  });

  const [schedules, setSchedules] = useState(
    (location.tariff_schedules || []).map(s => ({
      tempId: s.id,
      tariff_id: s.tariff_id,
      tariff_name: s.tariff_name,
      start_time: s.start_time || '',
      end_time: s.end_time || '',
      days_of_week: s.days_of_week || '',
      effective_start_date: s.effective_start_date || '',
      effective_end_date: s.effective_end_date || '',
      priority: s.priority || 0,
    }))
  );
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    tariff_id: '', start_time: '', end_time: '',
    days_of_week: '', effective_start_date: '', effective_end_date: '', priority: 0,
  });
  const [newShowDays, setNewShowDays] = useState(false);
  const [newShowDates, setNewShowDates] = useState(false);
  const [tariffs, setTariffs] = useState([]);
  const [tariffLookup, setTariffLookup] = useState({});
  const [unresolvedConflicts, setUnresolvedConflicts] = useState([]);
  const [gapWarnings, setGapWarnings] = useState([]);
  const [pendingSaveAttempted, setPendingSaveAttempted] = useState(false);

  // ── Charger & Connector editing state ──────────────────────────────────
  const [chargerEdits, setChargerEdits] = useState(
    (location.chargers || []).map(c => ({
      id: c.id,
      ocpp_id: c.ocpp_id || '',
      oem_name: c.oem_name || '',
      ocpp_version: c.ocpp_version || '',
      plug_and_charge: c.plug_and_charge || '',
      qr_code_id: c.qr_code_id || '',
      connectors: (c.connectors || []).map(conn => ({
        id: conn.id,
        connector_type: conn.connector_type || '',
        max_ampere: conn.max_ampere || '',
        max_voltage: conn.max_voltage || '',
        charging_speed: conn.charging_speed || '',
        energy_capacity: conn.energy_capacity || '',
        power_type: conn.power_type || '',
      })),
    }))
  );
  const [savingChargers, setSavingChargers] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tariffs/`).then(res => {
      setTariffs(res.data);
      const lookup = {};
      res.data.forEach(t => { lookup[t.id] = t.name; });
      setTariffLookup(lookup);
    }).catch(() => {});
  }, []);

  const handleChange = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const updateChargerField = (chargerId, field, value) => {
    setChargerEdits(prev => prev.map(c => c.id === chargerId ? { ...c, [field]: value } : c));
  };

  const updateConnectorField = (chargerId, connectorId, field, value) => {
    setChargerEdits(prev => prev.map(c =>
      c.id === chargerId
        ? { ...c, connectors: c.connectors.map(conn => conn.id === connectorId ? { ...conn, [field]: value } : conn) }
        : c
    ));
  };

  const saveChargersAndConnectors = async () => {
    setSavingChargers(true);
    try {
      for (const charger of chargerEdits) {
        await axios.patch(`${API}/chargers/${charger.id}`, {
          ocpp_id: charger.ocpp_id,
          oem_name: charger.oem_name || null,
          ocpp_version: charger.ocpp_version || null,
          plug_and_charge: charger.plug_and_charge || null,
          qr_code_id: charger.qr_code_id || null,
        });
        for (const conn of charger.connectors) {
          await axios.patch(`${API}/chargers/connectors/${conn.id}`, {
            connector_type: conn.connector_type,
            max_ampere: conn.max_ampere,
            max_voltage: conn.max_voltage || null,
            charging_speed: conn.charging_speed || null,
            energy_capacity: conn.energy_capacity || null,
            power_type: conn.power_type || null,
          });
        }
      }
      return true;
    } catch (err) {
      throw err;
    } finally {
      setSavingChargers(false);
    }
  };

  const addScheduleRow = () => {
    if (!newSchedule.tariff_id) return;
    const tariff = tariffs.find(t => String(t.id) === String(newSchedule.tariff_id));
    setSchedules(prev => [
      ...prev,
      {
        tempId: Date.now(),
        tariff_id: Number(newSchedule.tariff_id),
        tariff_name: tariff ? tariff.name : '',
        start_time: newSchedule.start_time,
        end_time: newSchedule.end_time,
        days_of_week: newSchedule.days_of_week,
        effective_start_date: newSchedule.effective_start_date,
        effective_end_date: newSchedule.effective_end_date,
        priority: 0,
      }
    ]);
    setNewSchedule({ tariff_id: '', start_time: '', end_time: '', days_of_week: '', effective_start_date: '', effective_end_date: '', priority: 0 });
    setNewShowDays(false);
    setNewShowDates(false);
    setShowAddSchedule(false);
    setPendingSaveAttempted(false);
    setUnresolvedConflicts([]);
    setGapWarnings([]);
  };

  const removeScheduleRow = (tempId) => {
    setSchedules(prev => prev.filter(s => s.tempId !== tempId));
    setPendingSaveAttempted(false);
    setUnresolvedConflicts([]);
    setGapWarnings([]);
  };

  const resolveConflict = (tempIdWinner) => {
    setSchedules(prev => {
      const updated = prev.map(s => s.tempId === tempIdWinner ? { ...s, priority: (s.priority || 0) + 1 } : s);
      const withTariff = updated.filter(s => s.tariff_id);
      if (withTariff.length >= 2) {
        const conflicts = findUnresolvedConflicts(withTariff, tariffLookup);
        setUnresolvedConflicts(conflicts.map(c => ({
          ...c, tempIdA: withTariff[c.indexA].tempId, tempIdB: withTariff[c.indexB].tempId,
        })));
      } else {
        setUnresolvedConflicts([]);
      }
      return updated;
    });
  };

  const isInConflict = (tempId) => unresolvedConflicts.some(c => c.tempIdA === tempId || c.tempIdB === tempId);

  const runValidation = () => {
    if (schedules.length < 2) {
      setUnresolvedConflicts([]);
      setGapWarnings(schedules.length > 0 ? findCoverageGaps(schedules) : []);
      return { conflicts: [], gaps: schedules.length > 0 ? findCoverageGaps(schedules) : [] };
    }
    const conflicts = findUnresolvedConflicts(schedules, tariffLookup);
    const mapped = conflicts.map(c => ({
      ...c,
      tempIdA: schedules[c.indexA].tempId,
      tempIdB: schedules[c.indexB].tempId,
    }));
    setUnresolvedConflicts(mapped);
    const gaps = findCoverageGaps(schedules);
    setGapWarnings(gaps);
    return { conflicts: mapped, gaps };
  };

  const handleSave = async () => {
    const { conflicts, gaps } = runValidation();
    if (conflicts.length > 0) return;
    if (gaps.length > 0 && !pendingSaveAttempted) {
      setPendingSaveAttempted(true);
      return;
    }

    try {
      await saveChargersAndConnectors();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save charger/connector changes');
      return;
    }

    const payload = {
      ...form,
      tariff_schedules: schedules.map(s => ({
        tariff_id: s.tariff_id,
        start_time: s.start_time || null,
        end_time: s.end_time || null,
        days_of_week: s.days_of_week || null,
        effective_start_date: s.effective_start_date || null,
        effective_end_date: s.effective_end_date || null,
        priority: s.priority || 0,
      }))
    };
    onSave(payload);
  };

  return (
    <div className="location-edit-panel">
      <div className="form-grid">
        {LOCATION_EDIT_FIELDS.map(field => (
          <div className="form-group" key={field.name}>
            <label>{field.label}</label>
            {field.type === 'select' ? (
              <select value={form[field.name]} onChange={(e) => handleChange(field.name, e.target.value)}>
                <option value="">Select...</option>
                {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : (
              <input type="text" value={form[field.name]} onChange={(e) => handleChange(field.name, e.target.value)} />
            )}
          </div>
        ))}
      </div>

      {/* ── Tariff Schedules ── */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 10 }}>Applied Tariffs</label>

        {unresolvedConflicts.map((conflict, i) => (
          <div className="collision-choice-box" key={i}>
            <div className="collision-choice-message">{conflict.message}</div>
            <div className="collision-choice-buttons">
              <button type="button" className="collision-choice-btn" onClick={() => resolveConflict(conflict.tempIdA)}>
                Give priority to {tariffLookup[schedules.find(s => s.tempId === conflict.tempIdA)?.tariff_id]}
              </button>
              <button type="button" className="collision-choice-btn" onClick={() => resolveConflict(conflict.tempIdB)}>
                Give priority to {tariffLookup[schedules.find(s => s.tempId === conflict.tempIdB)?.tariff_id]}
              </button>
            </div>
          </div>
        ))}

        {gapWarnings.length > 0 && unresolvedConflicts.length === 0 && (
          <div className="schedule-gap-banner">
            <div className="schedule-gap-banner-title">⚠ Some periods have no tariff assigned</div>
            <ul className="schedule-gap-banner-list">
              {gapWarnings.map((gap, i) => <li key={i}>{gap}</li>)}
            </ul>
          </div>
        )}

        {schedules.length === 0 && !showAddSchedule && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            No tariffs applied to this station yet.
          </div>
        )}

        {schedules.map(schedule => (
          <div className={`tariff-schedule-row ${isInConflict(schedule.tempId) ? 'has-conflict' : ''}`} key={schedule.tempId}>
            <div className="tariff-schedule-row-info">
              <div className="tariff-schedule-name">{schedule.tariff_name}</div>
              <div className="tariff-schedule-time">
                {schedule.days_of_week || 'All days'} · {schedule.start_time && schedule.end_time ? `${schedule.start_time}–${schedule.end_time}` : 'All hours'}
                {(schedule.effective_start_date || schedule.effective_end_date) &&
                  ` · From ${schedule.effective_start_date || 'now'} ${schedule.effective_end_date ? `to ${schedule.effective_end_date}` : '(ongoing)'}`}
              </div>
            </div>
            <button type="button" className="tariff-schedule-remove" onClick={() => removeScheduleRow(schedule.tempId)}>
              <IconTrash />
            </button>
          </div>
        ))}

        {showAddSchedule ? (
          <div className="tariff-schedule-form-row" style={{ flexWrap: 'wrap' }}>
            <div className="form-group">
              <label>Select Tariff</label>
              <select
                value={newSchedule.tariff_id}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, tariff_id: e.target.value }))}
              >
                <option value="">Select...</option>
                {tariffs.map(t => (
                  <option key={t.id} value={t.id}>{t.name} — {t.organisation_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Start Time</label>
              <TimeSelect
                value={newSchedule.start_time}
                onChange={(val) => setNewSchedule(prev => ({ ...prev, start_time: val }))}
              />
            </div>
            <div className="form-group">
              <label>End Time</label>
              <TimeSelect
                value={newSchedule.end_time}
                onChange={(val) => setNewSchedule(prev => ({ ...prev, end_time: val }))}
              />
            </div>
            <button type="button" className="btn btn-green" style={{ padding: '9px 16px' }} onClick={addScheduleRow}>
              Add
            </button>

            <div style={{ width: '100%', display: 'flex', gap: 20, marginTop: 4 }}>
              <label className="advanced-toggle">
                <input type="checkbox" checked={newShowDays} onChange={() => setNewShowDays(prev => !prev)} />
                Specific days
              </label>
              <label className="advanced-toggle">
                <input type="checkbox" checked={newShowDates} onChange={() => setNewShowDates(prev => !prev)} />
                Specific date range
              </label>
            </div>

            {newShowDays && (
              <div style={{ width: '100%' }}>
                <DayPicker
                  value={newSchedule.days_of_week}
                  onChange={(val) => setNewSchedule(prev => ({ ...prev, days_of_week: val }))}
                />
              </div>
            )}

            {newShowDates && (
              <div className="form-grid" style={{ width: '100%', gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={newSchedule.effective_start_date}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, effective_start_date: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={newSchedule.effective_end_date}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, effective_end_date: e.target.value }))}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="add-entity-btn"
            style={{ padding: 10, marginBottom: 0 }}
            onClick={() => setShowAddSchedule(true)}
          >
            <IconPlus /> Add Tariff
          </button>
        )}
      </div>

      {/* ── Chargers & Connectors ── */}
      {chargerEdits.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 10 }}>Chargers & Connectors</label>
          {chargerEdits.map(charger => (
            <div className="entity-card" key={charger.id}>
              <div className="entity-card-header">
                <span className="entity-card-title">{charger.ocpp_id || 'Charger'}</span>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>EVSE ID (OCPP ID)</label>
                  <input type="text" value={charger.ocpp_id} onChange={(e) => updateChargerField(charger.id, 'ocpp_id', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>OEM / Manufacturer</label>
                  <input type="text" value={charger.oem_name} onChange={(e) => updateChargerField(charger.id, 'oem_name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>OCPP Version</label>
                  <select value={charger.ocpp_version} onChange={(e) => updateChargerField(charger.id, 'ocpp_version', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="1.6">1.6</option>
                    <option value="2.0.1">2.0.1</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Plug and Charge</label>
                  <select value={charger.plug_and_charge} onChange={(e) => updateChargerField(charger.id, 'plug_and_charge', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div className="form-group full">
                  <label>QR Code ID</label>
                  <input type="text" value={charger.qr_code_id} onChange={(e) => updateChargerField(charger.id, 'qr_code_id', e.target.value)} placeholder="e.g. SRLK-QR-00001" />
                </div>
              </div>

              {charger.connectors.map((conn, i) => (
                <div key={conn.id} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--srlk-green-dark)', marginBottom: 8 }}>
                    Connector {i + 1}
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Connector Type</label>
                      <select value={conn.connector_type} onChange={(e) => updateConnectorField(charger.id, conn.id, 'connector_type', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="Type 2">Type 2</option>
                        <option value="CCS">CCS</option>
                        <option value="CHAdeMO">CHAdeMO</option>
                        <option value="Type 1">Type 1</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Max Ampere</label>
                      <input type="text" value={conn.max_ampere} onChange={(e) => updateConnectorField(charger.id, conn.id, 'max_ampere', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Max Voltage</label>
                      <input type="text" value={conn.max_voltage} onChange={(e) => updateConnectorField(charger.id, conn.id, 'max_voltage', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Charging Speed</label>
                      <select value={conn.charging_speed} onChange={(e) => updateConnectorField(charger.id, conn.id, 'charging_speed', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="Slow">Slow</option>
                        <option value="Fast">Fast</option>
                        <option value="Rapid">Rapid</option>
                        <option value="Ultra-Rapid">Ultra-Rapid</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Energy Capacity</label>
                      <input type="text" value={conn.energy_capacity} onChange={(e) => updateConnectorField(charger.id, conn.id, 'energy_capacity', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Power Type</label>
                      <select value={conn.power_type} onChange={(e) => updateConnectorField(charger.id, conn.id, 'power_type', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="AC">AC</option>
                        <option value="DC">DC</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="location-edit-actions">
        <button className="btn btn-green" onClick={handleSave} disabled={saving || savingChargers}>
          {saving || savingChargers ? 'Saving...' : (gapWarnings.length > 0 && pendingSaveAttempted ? 'Save Anyway' : 'Save Changes')}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function DayPicker({ value, onChange }) {
  const DAYS = [
    { key: 'Mon', label: 'Mon' }, { key: 'Tue', label: 'Tue' }, { key: 'Wed', label: 'Wed' },
    { key: 'Thu', label: 'Thu' }, { key: 'Fri', label: 'Fri' }, { key: 'Sat', label: 'Sat' }, { key: 'Sun', label: 'Sun' },
  ];
  const selectedDays = value ? value.split(',').map(d => d.trim()) : [];

  const toggleDay = (dayKey) => {
    const isSelected = selectedDays.includes(dayKey);
    const updated = isSelected ? selectedDays.filter(d => d !== dayKey) : [...selectedDays, dayKey];
    onChange(updated.length > 0 ? updated.join(',') : '');
  };

  return (
    <div className="day-picker">
      {DAYS.map(day => (
        <button
          type="button"
          key={day.key}
          className={`day-pill ${selectedDays.includes(day.key) ? 'selected' : ''}`}
          onClick={() => toggleDay(day.key)}
          title={day.key}
        >
          {day.label}
        </button>
      ))}
    </div>
  );
}

export default function OrganisationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { liveEvents, showToast } = useAppContext();

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLocations, setExpandedLocations] = useState({});
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locationDetails, setLocationDetails] = useState({});
  const [viewingQrFor, setViewingQrFor] = useState(null);
  const [viewingOcppFor, setViewingOcppFor] = useState(null);

  const fetchDetail = async () => {
    try {
      const res = await axios.get(`${API}/organisations/${id}/detail`);
      setOrg(res.data);
      if (res.data.locations.length > 0) {
        setExpandedLocations(prev =>
          Object.keys(prev).length === 0 ? { [res.data.locations[0].id]: true } : prev
        );
      }
    } catch (e) {
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationDetail = async (locationId) => {
    try {
      const res = await axios.get(`${API}/locations/${locationId}`);
      setLocationDetails(prev => ({ ...prev, [locationId]: res.data }));
    } catch (e) {
      console.error('Failed to fetch location detail', e);
    }
  };

  useEffect(() => { fetchDetail(); }, [id]);
  useEffect(() => { if (liveEvents.length > 0) fetchDetail(); }, [liveEvents]);

  useEffect(() => {
    if (org) {
      org.locations.forEach(loc => fetchLocationDetail(loc.id));
    }
  }, [org?.locations?.length]);

  const toggleLocation = (locationId) => {
    setExpandedLocations(prev => ({ ...prev, [locationId]: !prev[locationId] }));
  };

  const startEditing = (locationId) => {
    setEditingLocationId(locationId);
    setExpandedLocations(prev => ({ ...prev, [locationId]: true }));
  };

  const handleSaveEdit = async (locationId, payload) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/locations/${locationId}`, payload);
      showToast('Station updated successfully');
      setEditingLocationId(null);
      await fetchDetail();
      await fetchLocationDetail(locationId);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update station', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStation = async (loc) => {
    if (!window.confirm(`Delete "${loc.station_name}"? This will permanently remove this station and all ${loc.chargers.length} charger(s) and their connectors. This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/locations/${loc.id}`);
      showToast(`Station "${loc.station_name}" removed successfully`);
      await fetchDetail();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete station', 'error');
    }
  };

  if (loading) return <div className="loading">Loading organisation...</div>;
  if (!org) return null;

  return (
    <>
      <button className="back-link" onClick={() => navigate('/')}>← Back to Overview</button>

      <div className="org-detail-header">
        <div className="org-detail-icon-row">
          <div className="org-detail-icon"><IconBuilding /></div>
          <div className="org-detail-title-block">
            <h1>{org.name}</h1>
            <p>{org.location_count} location{org.location_count !== 1 ? 's' : ''} · {org.charger_count} charger{org.charger_count !== 1 ? 's' : ''} · {org.connector_count} connector{org.connector_count !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button className="btn btn-green" onClick={() => navigate('/add-charger')}>
          + Add EVSE
        </button>
      </div>

      {org.locations.length === 0 ? (
        <div className="card empty-state">
          <h3>No locations yet</h3>
          <p>This organisation has no charging stations registered.</p>
          <button className="btn btn-green" onClick={() => navigate('/add-charger')}>
            + Add EVSE
          </button>
        </div>
      ) : (
        org.locations.map(loc => {
          const isOpen = !!expandedLocations[loc.id];
          const isEditing = editingLocationId === loc.id;
          const totalConnectors = loc.chargers.reduce((sum, c) => sum + c.connectors.length, 0);
          const activeChargers = loc.chargers.filter(c => c.status === 'Available' || c.status === 'Charging').length;
          const detail = locationDetails[loc.id];
          const schedules = detail?.tariff_schedules || [];

          return (
            <div className="location-block" key={loc.id}>
              <div className="location-block-header" onClick={() => !isEditing && toggleLocation(loc.id)}>
                <div className="location-block-title-row">
                  <div className="location-block-icon"><IconPin /></div>
                  <div>
                    <div className="location-block-title">
                      {loc.station_name}
                      {schedules.length > 0 ? (
                        schedules.map((s, i) => (
                          <span className="tariff-tag" key={i}>
                            <IconTag /> {s.tariff_name}
                            {s.start_time && s.end_time && ` (${s.start_time}–${s.end_time})`}
                          </span>
                        ))
                      ) : (
                        <span className="tariff-tag none"><IconTag /> No tariff</span>
                      )}
                    </div>
                    <div className="location-block-meta">
                      {[loc.address, loc.city, loc.country].filter(Boolean).join(', ') || 'No address on file'}
                      {' · '}{loc.chargers.length} charger{loc.chargers.length !== 1 ? 's' : ''}
                      {' · '}{totalConnectors} connector{totalConnectors !== 1 ? 's' : ''}
                      {activeChargers > 0 && ` · ${activeChargers} online`}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {!isEditing && (
                    <>
                      <button
                        className="location-edit-toggle-btn"
                        onClick={(e) => { e.stopPropagation(); navigate(`/load-management/${loc.id}`); }}
                      >
                        Load Management
                      </button>
                      <button
                        className="location-edit-toggle-btn"
                        onClick={(e) => { e.stopPropagation(); startEditing(loc.id); }}
                      >
                        <IconEdit /> Edit
                      </button>
                      <button
                        className="location-edit-toggle-btn"
                        style={{ color: 'var(--error-500)' }}
                        onClick={(e) => { e.stopPropagation(); handleDeleteStation(loc); }}
                      >
                        <IconTrashStation /> Delete
                      </button>
                    </>
                  )}
                  <span className={`location-block-chevron ${isOpen ? 'open' : ''}`}>
                    <IconChevron />
                  </span>
                </div>
              </div>

              {isEditing && detail ? (
                <LocationEditPanel
                  location={detail}
                  saving={saving}
                  onSave={(payload) => handleSaveEdit(loc.id, payload)}
                  onCancel={() => setEditingLocationId(null)}
                />
              ) : isOpen && (
                <div className="location-block-body">
                  {loc.chargers.length === 0 ? (
                    <div style={{ padding: '16px 0', color: 'var(--text-muted)', fontSize: 13.5 }}>
                      No chargers added to this location yet.
                    </div>
                  ) : (
                    loc.chargers.map(charger => (
                      <div className="charger-row" key={charger.id}>
                        <div className="charger-row-left">
                          <div className="charger-row-icon"><IconPlugSmall /></div>
                          <div>
                            <div className="charger-row-id">{charger.ocpp_id}</div>
                            <div className="charger-row-connectors">
                              {charger.connectors.length === 0 ? (
                                'No connectors'
                              ) : (
                                charger.connectors.map((c, i) => (
                                  <span className="connector-chip" key={i}>{c.connector_type}</span>
                                ))
                              )}
                              {charger.oem_name && <span style={{ marginLeft: 4 }}>{charger.oem_name}</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <StatusBadge status={charger.status} />
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {timeAgo(charger.last_seen)}
                          </span>
                          {charger.qr_code_id && (
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '5px 12px', fontSize: 12 }}
                              onClick={() => setViewingQrFor(charger)}
                            >
                              <IconQrCode /> QR Code
                            </button>
                          )}
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '5px 12px', fontSize: 12 }}
                            onClick={() => setViewingOcppFor(charger)}
                          >
                            OCPP Setup
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '5px 12px', fontSize: 12 }}
                            onClick={() => navigate(`/charger/${charger.id}`)}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      {viewingQrFor && (
        <QrCodeModal charger={viewingQrFor} onClose={() => setViewingQrFor(null)} />
      )}

      {viewingOcppFor && (
        <OcppSetupModal charger={viewingOcppFor} onClose={() => setViewingOcppFor(null)} />
      )}
    </>
  );
}